/**
 * Figure layout.
 *
 * A fixed viewBox scaled by CSS: the aspect ratio is then a property of the
 * figure rather than of the reader's window, which keeps `equalAspect` honest
 * and removes any need to measure the DOM.
 */

import type { PlotSpec } from "../../shared/plot-spec.ts";

export interface Layout {
  readonly width: number;
  readonly height: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
  readonly innerWidth: number;
  readonly innerHeight: number;
  /** width / height of the plotting area — what `equalAspect` matches against. */
  readonly aspect: number;
}

const VIEW_WIDTH = 760;
const VIEW_HEIGHT = 450;

export function computeLayout(_spec: PlotSpec): Layout {
  /**
   * Only tick labels live inside the SVG now. The title, axis labels, and
   * legend are HTML sitting around it, so they can be typeset with KaTeX —
   * an SVG `<text>` node can hold a string but not mathematics.
   */
  const left = 58;
  const right = 22;
  const top = 18;
  const bottom = 42;

  const innerWidth = VIEW_WIDTH - left - right;
  const innerHeight = VIEW_HEIGHT - top - bottom;

  return {
    width: VIEW_WIDTH,
    height: VIEW_HEIGHT,
    left,
    right,
    top,
    bottom,
    innerWidth,
    innerHeight,
    aspect: innerWidth / innerHeight,
  };
}

/** Slider values, seeded from the spec's declared defaults. */
export function initialParams(spec: PlotSpec): Record<string, number> {
  return Object.fromEntries(spec.params.map((param) => [param.name, param.value]));
}

/** A sensible step when the spec doesn't declare one. */
export function paramStep(min: number, max: number, declared?: number): number {
  if (declared && declared > 0) return declared;
  const span = Math.abs(max - min);
  if (span === 0) return 1;
  const raw = span / 100;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return Math.max(magnitude, 1e-6);
}
